import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowDown, ArrowRight, ArrowUpRight, Check, Sparkles } from 'lucide-react';
import { portfolioProjects } from '../data/portfolio';
import { assignExperiment, HOMEPAGE_HERO_EXPERIMENT, trackEvent } from '../services/analytics';

const capabilities = ['Strategy', 'Product', 'Engineering', 'Applied AI'];
const workflow = ['Frame', 'Design', 'Build', 'Learn'];

const heroVariants = [
  { id: 'editorial', label: 'Editorial' },
  { id: 'constellation', label: 'Constellation' },
  { id: 'proof', label: 'Proof first' },
  { id: 'system', label: 'Delivery system' },
] as const;

type HeroVariantId = (typeof heroVariants)[number]['id'];
const heroVariantIds = heroVariants.map(variant => variant.id);

const isHeroVariant = (value: string | null): value is HeroVariantId =>
  heroVariants.some(variant => variant.id === value);

const HeroActions = ({ centered = false }: { centered?: boolean }) => (
  <div className={`flex flex-col gap-4 sm:flex-row ${centered ? 'sm:justify-center' : ''}`}>
    <a
      href="#portfolio"
      data-agent-id="agent://consltr/hero/view-work"
      data-semantic-intent="navigate"
      data-track="hero_view_selected_work"
      className="hero-primary-cta group inline-flex items-center justify-center gap-2 rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-canvas hover:text-black"
    >
      See selected work
      <ArrowDown
        size={16}
        className="transition-transform duration-300 group-hover:translate-y-1"
        aria-hidden="true"
      />
    </a>
    <a
      href="#contact"
      data-agent-id="agent://consltr/hero/start-conversation"
      data-semantic-intent="navigate"
      data-track="hero_start_conversation"
      className="group inline-flex items-center justify-center gap-2 rounded-full border border-line/20 bg-canvas/10 px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:border-line/50 hover:bg-ink/[0.07]"
    >
      Talk through an idea
      <ArrowUpRight
        size={16}
        className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        aria-hidden="true"
      />
    </a>
  </div>
);

const EditorialHero = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <div className="mx-auto grid min-h-[calc(100vh-9rem)] max-w-7xl content-center px-6 py-16 lg:px-8">
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduceMotion ? 0 : 0.65 }}
      className="mb-9 inline-flex w-fit items-center gap-3 rounded-full border border-line/15 bg-ink/[0.04] px-4 py-2 text-sm text-muted backdrop-blur-md"
    >
      <span className="hero-status-dot h-2 w-2 rounded-full bg-highlight" />
      Independent product & technology consultancy
    </motion.div>

    <motion.h1
      initial={reduceMotion ? false : { opacity: 0, y: 34 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: reduceMotion ? 0 : 0.12, duration: reduceMotion ? 0 : 0.8 }}
      className="magazine-title max-w-6xl text-[clamp(4rem,10vw,9.5rem)] leading-[0.86] tracking-[-0.065em]"
    >
      From hard idea
      <span className="hero-gradient-text block">to working product.</span>
    </motion.h1>

    <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-end">
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.3, duration: reduceMotion ? 0 : 0.65 }}
      >
        <HeroActions />
      </motion.div>

      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.42, duration: reduceMotion ? 0 : 0.65 }}
      >
        <p className="max-w-2xl text-xl leading-8 text-muted md:text-2xl md:leading-9">
          Consltr works with founders and teams to clarify the opportunity, design the right system,
          and build software people can actually use.
        </p>
        <div className="mt-8 grid grid-cols-2 border-y border-line/10 sm:grid-cols-4">
          {capabilities.map((capability, index) => (
            <div
              key={capability}
              className="hero-capability border-line/10 py-4 text-sm font-medium text-muted sm:border-l sm:px-4 first:sm:border-l-0 first:sm:pl-0"
              style={{ animationDelay: `${0.68 + index * 0.08}s` }}
            >
              <span className="mr-2 font-mono text-xs text-faint">0{index + 1}</span>
              {capability}
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  </div>
);

const ConstellationHero = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <div className="mx-auto grid min-h-[calc(100vh-9rem)] max-w-7xl items-center gap-10 px-6 py-12 lg:grid-cols-[0.95fr_1.05fr] lg:px-8">
    <div className="relative z-10">
      <motion.p
        initial={reduceMotion ? false : { opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="section-kicker"
      >
        One connected team
      </motion.p>
      <motion.h1
        initial={reduceMotion ? false : { opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.1, duration: reduceMotion ? 0 : 0.75 }}
        className="magazine-title mt-5 text-[clamp(4rem,8vw,8rem)] leading-[0.88] tracking-[-0.06em]"
      >
        One partner.
        <span className="hero-gradient-text block">Every hard part.</span>
      </motion.h1>
      <motion.p
        initial={reduceMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.25 }}
        className="mt-8 max-w-xl text-lg leading-8 text-muted"
      >
        Strategy, experience, engineering, and AI move together—so decisions travel cleanly from the
        first working session to launch day.
      </motion.p>
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.38 }}
        className="mt-9"
      >
        <HeroActions />
      </motion.div>
    </div>

    <motion.div
      initial={reduceMotion ? false : { opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: reduceMotion ? 0 : 0.18, duration: reduceMotion ? 0 : 0.85 }}
      className="hero-orbit-shell mx-auto"
      aria-label="Consltr capabilities connected to selected work"
    >
      <div className="hero-orbit-glow" aria-hidden="true" />
      <div className="hero-orbit-ring hero-orbit-ring-one" aria-hidden="true" />
      <div className="hero-orbit-ring hero-orbit-ring-two" aria-hidden="true" />
      <div className="hero-orbit-core">
        <span className="font-mono text-[0.62rem] uppercase tracking-[0.22em] text-faint">
          Product partner
        </span>
        <strong className="serif mt-2 text-4xl font-normal">Consltr.</strong>
        <span className="mt-2 text-sm text-muted">Idea → outcome</span>
      </div>
      {portfolioProjects.map((project, index) => (
        <a
          key={project.id}
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`hero-orbit-node hero-orbit-node-${index + 1}`}
          style={{ '--node-color': project.accent } as CSSProperties}
          aria-label={`View ${project.name}`}
        >
          <span>{project.mark}</span>
          <small>{project.name.replace(' Bay Area', '')}</small>
        </a>
      ))}
    </motion.div>
  </div>
);

const ProofHero = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <div className="mx-auto grid min-h-[calc(100vh-9rem)] max-w-7xl items-center gap-16 px-6 py-12 lg:grid-cols-[1fr_0.9fr] lg:px-8">
    <div>
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8 flex flex-wrap items-center gap-3 text-sm text-muted"
      >
        <span className="rounded-full bg-highlight px-3 py-1.5 font-mono text-[0.65rem] font-bold uppercase tracking-[0.16em] text-black">
          Six live products
        </span>
        <span>Built across commerce, AI, community, collaboration, and culture.</span>
      </motion.div>
      <motion.h1
        initial={reduceMotion ? false : { opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.12, duration: reduceMotion ? 0 : 0.8 }}
        className="magazine-title text-[clamp(4rem,9vw,8.5rem)] leading-[0.86] tracking-[-0.065em]"
      >
        The strategy
        <span className="block text-faint">is in the</span>
        <span className="hero-gradient-text block">shipping.</span>
      </motion.h1>
      <motion.p
        initial={reduceMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.28 }}
        className="mt-8 max-w-2xl text-lg leading-8 text-muted"
      >
        Consltr pairs senior product judgment with hands-on delivery. The result is not another
        recommendation deck—it is a product in the world, learning from real people.
      </motion.p>
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : 0.4 }}
        className="mt-9"
      >
        <HeroActions />
      </motion.div>
    </div>

    <motion.div
      initial={reduceMotion ? false : { opacity: 0, x: 40 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: reduceMotion ? 0 : 0.2, duration: reduceMotion ? 0 : 0.8 }}
      className="hero-proof-stack"
    >
      <div className="hero-proof-stat">
        <Sparkles size={17} aria-hidden="true" />
        <span>Selected work / 2024—now</span>
      </div>
      {portfolioProjects.slice(0, 3).map((project, index) => (
        <a
          key={project.id}
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`hero-proof-card hero-proof-card-${index + 1}`}
          style={
            {
              '--proof-accent': project.accent,
              '--proof-surface': project.surface,
              '--proof-foreground': project.foreground,
            } as CSSProperties
          }
        >
          <span className="hero-proof-mark">{project.mark}</span>
          <div>
            <small>{project.category}</small>
            <strong>{project.name}</strong>
          </div>
          <ArrowUpRight size={20} aria-hidden="true" />
        </a>
      ))}
      <div className="hero-proof-list" aria-label="Additional portfolio companies">
        {portfolioProjects.slice(3).map(project => (
          <span key={project.id}>
            <Check size={13} aria-hidden="true" />
            {project.name}
          </span>
        ))}
      </div>
    </motion.div>
  </div>
);

const SystemHero = ({ reduceMotion }: { reduceMotion: boolean }) => (
  <div className="mx-auto flex min-h-[calc(100vh-9rem)] max-w-7xl flex-col justify-center px-6 py-14 lg:px-8">
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col justify-between gap-6 border-b border-line/10 pb-7 sm:flex-row sm:items-end"
    >
      <p className="section-kicker">A practical delivery system</p>
      <p className="max-w-md text-sm leading-6 text-faint sm:text-right">
        One accountable partner from ambiguous opportunity to a product that works in the real
        world.
      </p>
    </motion.div>

    <motion.h1
      initial={reduceMotion ? false : { opacity: 0, y: 36 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: reduceMotion ? 0 : 0.12, duration: reduceMotion ? 0 : 0.85 }}
      className="magazine-title my-10 max-w-7xl text-[clamp(4rem,10.5vw,10rem)] leading-[0.82] tracking-[-0.07em]"
    >
      Clarity to build.
      <span className="hero-gradient-text block text-right">Craft to launch.</span>
    </motion.h1>

    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: reduceMotion ? 0 : 0.28, duration: reduceMotion ? 0 : 0.7 }}
      className="hero-workflow"
    >
      <div className="hero-workflow-line" aria-hidden="true" />
      {workflow.map((step, index) => (
        <div key={step} className="hero-workflow-step">
          <span>0{index + 1}</span>
          <strong>{step}</strong>
          <small>
            {['The opportunity', 'The experience', 'The product', 'The evidence'][index]}
          </small>
        </div>
      ))}
    </motion.div>

    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: reduceMotion ? 0 : 0.5 }}
      className="mt-10 flex flex-col gap-7 md:flex-row md:items-center md:justify-between"
    >
      <HeroActions />
      <a
        href="#services"
        className="group inline-flex items-center gap-3 text-sm font-semibold text-muted transition-colors hover:text-ink"
      >
        Explore the practices
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-line/15 transition-all group-hover:border-accent group-hover:text-accent">
          <ArrowRight size={15} aria-hidden="true" />
        </span>
      </a>
    </motion.div>
  </div>
);

const Hero = () => {
  // Editorial is the deterministic server/first-client render. URL overrides and
  // Salsa assignment are applied immediately after hydration.
  const [activeVariant, setActiveVariant] = useState<HeroVariantId>('editorial');
  const shouldReduceMotion = Boolean(useReducedMotion());

  useEffect(() => {
    const requestedVariant = new URLSearchParams(window.location.search).get('hero');
    if (isHeroVariant(requestedVariant)) {
      setActiveVariant(requestedVariant);
      return;
    }

    const assignment = assignExperiment(HOMEPAGE_HERO_EXPERIMENT, heroVariantIds, {
      surface: 'homepage_hero',
    });
    if (assignment) setActiveVariant(assignment);
  }, []);

  const selectVariant = (variant: HeroVariantId) => {
    setActiveVariant(variant);
    trackEvent('experiment_variant_selected', {
      experiment_key: HOMEPAGE_HERO_EXPERIMENT,
      variant,
      surface: 'homepage_hero',
    });
    if (typeof window === 'undefined') return;
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('hero', variant);
    window.history.replaceState({}, '', nextUrl);
  };

  return (
    <section
      id="top"
      className="hero-stage relative overflow-hidden pt-24"
      data-experiment={HOMEPAGE_HERO_EXPERIMENT}
      data-experiment-variants={heroVariantIds.join(',')}
      data-variant={activeVariant}
    >
      <div className="hero-grid absolute inset-0 opacity-60" aria-hidden="true" />
      <div className="hero-ambient hero-ambient-one" aria-hidden="true" />
      <div className="hero-ambient hero-ambient-two" aria-hidden="true" />

      <div className="relative z-20 mx-auto flex max-w-7xl items-center gap-4 px-6 pt-5 lg:px-8">
        <span className="hidden font-mono text-[0.62rem] uppercase tracking-[0.18em] text-faint sm:block">
          Hero direction
        </span>
        <div
          className="hero-variant-picker flex max-w-full gap-1 overflow-x-auto rounded-full border border-line/10 bg-canvas/70 p-1 backdrop-blur-xl"
          role="group"
          aria-label="Choose a hero direction"
        >
          {heroVariants.map((variant, index) => (
            <button
              key={variant.id}
              type="button"
              onClick={() => selectVariant(variant.id)}
              data-agent-id={`agent://consltr/hero/variant/${variant.id}`}
              data-semantic-intent="select"
              data-track={`hero_variant_${variant.id}`}
              className={`relative shrink-0 rounded-full px-3.5 py-2 text-xs font-semibold transition-colors sm:px-4 ${
                activeVariant === variant.id ? 'text-black' : 'text-faint hover:text-ink'
              }`}
              aria-pressed={activeVariant === variant.id}
            >
              {activeVariant === variant.id && (
                <motion.span
                  layoutId="active-hero-variant"
                  className="absolute inset-0 rounded-full bg-highlight"
                  transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                />
              )}
              <span className="relative z-10">
                0{index + 1} {variant.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={activeVariant}
          initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={shouldReduceMotion ? undefined : { opacity: 0, y: -10 }}
          transition={{ duration: shouldReduceMotion ? 0 : 0.32 }}
          className="relative z-10"
        >
          {activeVariant === 'editorial' && <EditorialHero reduceMotion={shouldReduceMotion} />}
          {activeVariant === 'constellation' && (
            <ConstellationHero reduceMotion={shouldReduceMotion} />
          )}
          {activeVariant === 'proof' && <ProofHero reduceMotion={shouldReduceMotion} />}
          {activeVariant === 'system' && <SystemHero reduceMotion={shouldReduceMotion} />}
        </motion.div>
      </AnimatePresence>
    </section>
  );
};

export default Hero;
